(() => {
  if (customElements.get('ff-b2b-volume-calculator')) return;

  class FfB2bVolumeCalculator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized) return;
      this.dataset.initialized = 'true';

      this.usageRoot = this.querySelector('[data-ff-b2b-calculator="usage"]');
      this.salesRoot = this.querySelector('[data-ff-b2b-calculator="sales"]');
      this.setupRouteChooser();
      this.setupUsageCalculator();
      this.setupPrivateLabelConfigurator();
    }

    setupRouteChooser() {
      this.routeButtons = [...this.querySelectorAll('[data-ff-b2b-route-choice]')];
      const panels = [this.usageRoot, this.salesRoot].filter(Boolean);
      if (!this.routeButtons.length || !panels.length) return;

      panels.forEach((panel) => { panel.hidden = true; });
      this.routeButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const route = button.dataset.ffB2bRouteChoice;
          panels.forEach((panel) => {
            panel.hidden = panel.dataset.ffB2bCalculator !== route;
          });
          this.routeButtons.forEach((routeButton) => {
            routeButton.setAttribute('aria-expanded', String(routeButton === button));
          });
          const activePanel = route === 'sales' ? this.salesRoot : this.usageRoot;
          activePanel?.querySelector('input, select, button')?.focus({ preventScroll: true });
        });
      });
    }

    setupUsageCalculator() {
      if (!this.usageRoot) return;

      this.usage = {
        loads: this.usageRoot.querySelector('[data-ff-b2b-usage-loads]'),
        days: this.usageRoot.querySelector('[data-ff-b2b-usage-days]'),
        dose: this.usageRoot.querySelector('[data-ff-b2b-usage-dose]'),
        period: this.usageRoot.querySelector('[data-ff-b2b-usage-period]'),
        weekly: this.usageRoot.querySelector('[data-ff-b2b-usage-weekly]'),
        monthly: this.usageRoot.querySelector('[data-ff-b2b-usage-monthly]'),
        annual: this.usageRoot.querySelector('[data-ff-b2b-usage-annual]'),
        quote: this.usageRoot.querySelector('[data-ff-b2b-usage-quote]'),
        route: this.usageRoot.querySelector('[data-ff-b2b-usage-route]'),
        routeText: this.usageRoot.querySelector('[data-ff-b2b-usage-route-text]'),
        useButton: this.usageRoot.querySelector('[data-ff-b2b-usage-use]'),
        confirmation: this.usageRoot.querySelector('[data-ff-b2b-usage-confirmation]'),
      };

      [this.usage.loads, this.usage.days, this.usage.dose, this.usage.period]
        .filter(Boolean)
        .forEach((input) => input.addEventListener('input', () => this.updateUsage()));

      this.usage.useButton?.addEventListener('click', () => {
        this.addToQuote(this.usageCalculation, this.usage.confirmation);
      });
      this.updateUsage();
    }

    setupPrivateLabelConfigurator() {
      if (!this.salesRoot) return;

      this.sales = {
        projects: [...this.salesRoot.querySelectorAll('[data-ff-b2b-config-project]')],
        formula: this.salesRoot.querySelector('[data-ff-b2b-config-formula]'),
        scent: this.salesRoot.querySelector('[data-ff-b2b-config-scent]'),
        weight: this.salesRoot.querySelector('[data-ff-b2b-config-weight]'),
        customWeight: this.salesRoot.querySelector('[data-ff-b2b-config-custom-weight]'),
        customWeightField: this.salesRoot.querySelector('[data-ff-b2b-custom-weight-field]'),
        weightNote: this.salesRoot.querySelector('[data-ff-b2b-weight-note]'),
        shapes: [...this.salesRoot.querySelectorAll('[data-ff-b2b-config-shape]')],
        packaging: this.salesRoot.querySelector('[data-ff-b2b-config-packaging]'),
        brand: this.salesRoot.querySelector('[data-ff-b2b-config-brand]'),
        brandingModes: [...this.salesRoot.querySelectorAll('[data-ff-b2b-config-branding]')],
        languages: [...this.salesRoot.querySelectorAll('[data-ff-b2b-config-language]')],
        packages: this.salesRoot.querySelector('[data-ff-b2b-sales-packages]'),
        packSize: this.salesRoot.querySelector('[data-ff-b2b-sales-pack-size]'),
        period: this.salesRoot.querySelector('[data-ff-b2b-sales-period]'),
        buffer: this.salesRoot.querySelector('[data-ff-b2b-sales-buffer]'),
        packagesPeriod: this.salesRoot.querySelector('[data-ff-b2b-sales-packages-period]'),
        monthly: this.salesRoot.querySelector('[data-ff-b2b-sales-monthly]'),
        annual: this.salesRoot.querySelector('[data-ff-b2b-sales-annual]'),
        forecast: this.salesRoot.querySelector('[data-ff-b2b-sales-forecast]'),
        quote: this.salesRoot.querySelector('[data-ff-b2b-sales-quote]'),
        route: this.salesRoot.querySelector('[data-ff-b2b-sales-route]'),
        routeText: this.salesRoot.querySelector('[data-ff-b2b-sales-route-text]'),
        useButton: this.salesRoot.querySelector('[data-ff-b2b-sales-use]'),
        confirmation: this.salesRoot.querySelector('[data-ff-b2b-sales-confirmation]'),
        previewPod: this.salesRoot.querySelector('[data-ff-b2b-preview-pod]'),
        previewPack: this.salesRoot.querySelector('[data-ff-b2b-preview-pack]'),
        previewBadge: this.salesRoot.querySelector('[data-ff-b2b-preview-pack] small'),
        previewBrand: this.salesRoot.querySelector('[data-ff-b2b-preview-brand]'),
        previewPackaging: this.salesRoot.querySelector('[data-ff-b2b-preview-packaging]'),
        previewPackSize: this.salesRoot.querySelector('[data-ff-b2b-preview-pack-size]'),
        previewProject: this.salesRoot.querySelector('[data-ff-b2b-preview-project]'),
        previewFormula: this.salesRoot.querySelector('[data-ff-b2b-preview-formula]'),
        previewShape: this.salesRoot.querySelector('[data-ff-b2b-preview-shape]'),
        previewPackagingSummary: this.salesRoot.querySelector('[data-ff-b2b-preview-packaging-summary]'),
        previewLanguages: this.salesRoot.querySelector('[data-ff-b2b-preview-languages]'),
      };

      [
        ...this.sales.projects,
        this.sales.formula,
        this.sales.scent,
        this.sales.weight,
        this.sales.customWeight,
        ...this.sales.shapes,
        this.sales.packaging,
        this.sales.brand,
        ...this.sales.brandingModes,
        ...this.sales.languages,
        this.sales.packages,
        this.sales.packSize,
        this.sales.period,
        this.sales.buffer,
      ]
        .filter(Boolean)
        .forEach((input) => input.addEventListener('input', () => this.updateSales()));

      this.sales.useButton?.addEventListener('click', () => {
        this.addToQuote(this.salesCalculation, this.sales.confirmation);
      });
      this.setupConfiguratorSteps();
      this.updateSales();
    }

    setupConfiguratorSteps() {
      this.wizard = {
        steps: [...this.salesRoot.querySelectorAll('[data-ff-b2b-step]')],
        current: this.salesRoot.querySelector('[data-ff-b2b-step-current]'),
        progress: this.salesRoot.querySelector('[data-ff-b2b-step-progress]'),
        previous: this.salesRoot.querySelector('[data-ff-b2b-step-previous]'),
        next: this.salesRoot.querySelector('[data-ff-b2b-step-next]'),
        active: 1,
      };
      if (!this.wizard.steps.length) return;

      this.wizard.previous?.addEventListener('click', () => this.showConfiguratorStep(this.wizard.active - 1, true));
      this.wizard.next?.addEventListener('click', () => this.showConfiguratorStep(this.wizard.active + 1, true));
      this.showConfiguratorStep(1, false);
    }

    showConfiguratorStep(stepNumber, moveFocus) {
      if (!this.wizard?.steps?.length) return;
      const total = this.wizard.steps.length;
      const active = Math.min(total, Math.max(1, stepNumber));
      this.wizard.active = active;
      this.wizard.steps.forEach((step) => {
        step.hidden = Number(step.dataset.ffB2bStep) !== active;
      });
      if (this.wizard.current) this.wizard.current.textContent = String(active);
      if (this.wizard.progress) {
        this.wizard.progress.setAttribute('aria-valuenow', String(active));
        this.wizard.progress.querySelector('span')?.style.setProperty('width', `${(active / total) * 100}%`);
      }
      if (this.wizard.previous) this.wizard.previous.hidden = active === 1;
      if (this.wizard.next) this.wizard.next.hidden = active === total;

      if (moveFocus) {
        const heading = this.wizard.steps.find((step) => Number(step.dataset.ffB2bStep) === active)
          ?.querySelector('legend, h4');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }
      }
    }

    getNumber(input, fallback, minimum = 1, maximum = Number.MAX_SAFE_INTEGER) {
      const number = Number(input?.value);
      if (!Number.isFinite(number)) return fallback;
      return Math.min(maximum, Math.max(minimum, number));
    }

    roundUp(value, increment) {
      return Math.ceil(value / increment) * increment;
    }

    getLocale() {
      const localeMap = { nl: 'nl-NL', en: 'en-GB', de: 'de-DE', fr: 'fr-FR', es: 'es-ES' };
      return localeMap[this.dataset.locale] || 'nl-NL';
    }

    formatNumber(value) {
      return new Intl.NumberFormat(this.getLocale(), { maximumFractionDigits: 0 }).format(value);
    }

    getUsageQuoteVolume(plannedCapsules, route) {
      if (route === 'bulk') return Math.max(25000, this.roundUp(plannedCapsules, 5000));
      return Math.max(800, this.roundUp(plannedCapsules, 800));
    }

    selectedRadio(inputs, fallback) {
      return inputs.find((input) => input.checked)?.value || fallback;
    }

    optionLabel(select) {
      return select?.selectedOptions?.[0]?.textContent?.trim() || '';
    }

    radioLabel(input) {
      if (!input?.id) return '';
      const label = this.salesRoot.querySelector(`label[for="${input.id}"]`);
      return label?.querySelector('strong')?.textContent?.trim() || label?.textContent?.trim() || '';
    }

    updateUsage() {
      const loadsPerDay = this.getNumber(this.usage.loads, 12, 1, 1000);
      const daysPerWeek = this.getNumber(this.usage.days, 5, 1, 7);
      const dose = this.getNumber(this.usage.dose, 1, 1, 20);
      const periodMonths = this.getNumber(this.usage.period, 1, 1, 12);
      const weeklyLoads = Math.round(loadsPerDay * daysPerWeek);
      const annualCapsules = Math.round(weeklyLoads * dose * 52);
      const monthlyCapsules = Math.round(annualCapsules / 12);
      const periodCapsules = Math.round(monthlyCapsules * periodMonths);
      const route = annualCapsules >= 25000 ? 'bulk' : 'standard';
      const quoteVolume = this.getUsageQuoteVolume(periodCapsules, route);

      this.usageCalculation = {
        kind: 'usage', loadsPerDay, daysPerWeek, dose, periodMonths, weeklyLoads,
        monthlyCapsules, annualCapsules, periodCapsules, quoteVolume, route,
      };

      this.usage.weekly.textContent = this.formatNumber(weeklyLoads);
      this.usage.monthly.textContent = this.formatNumber(monthlyCapsules);
      this.usage.annual.textContent = this.formatNumber(annualCapsules);
      this.usage.quote.textContent = `${this.formatNumber(quoteVolume)} ${this.dataset.capsuleUnit}`;
      const routeKey = this.capitalize(route);
      this.usage.route.textContent = this.dataset[`route${routeKey}`];
      this.usage.routeText.textContent = this.dataset[`route${routeKey}Text`];
      this.hideConfirmations();
    }

    updateSales() {
      const projectType = this.selectedRadio(this.sales.projects, 'private');
      const projectInput = this.sales.projects.find((input) => input.checked);
      const projectLabel = this.radioLabel(projectInput);
      const formula = this.sales.formula?.value || 'standard';
      const scent = this.sales.scent?.value || 'tbd';
      const shapeInput = this.sales.shapes.find((input) => input.checked);
      const shape = shapeInput?.value || '1';
      const shapeLabel = this.radioLabel(shapeInput);
      const standardWeightOption = this.sales.weight?.querySelector('option[value="standard"]');
      const standardWeightLabel = standardWeightOption?.dataset.defaultLabel || standardWeightOption?.textContent?.trim() || '';
      const standardWeights = shapeInput?.dataset.standardWeights || '';
      if (standardWeightOption) {
        standardWeightOption.textContent = standardWeights
          ? `${standardWeightLabel}: ${standardWeights}`
          : standardWeightLabel;
      }
      const packaging = this.sales.packaging?.value || 'kraft';
      const selectedPackaging = this.sales.packaging?.selectedOptions?.[0];
      const packagingLabel = this.optionLabel(this.sales.packaging);
      const packagingFormValue = selectedPackaging?.dataset.formValue || packagingLabel;
      const brandingMode = this.selectedRadio(this.sales.brandingModes, 'artwork');
      const brand = this.sales.brand?.value?.trim() || this.sales.brand?.defaultValue || '';
      const languages = this.sales.languages
        .filter((input) => input.checked)
        .map((input) => input.dataset.label || input.value.toUpperCase());
      const packagesPerMonth = this.getNumber(this.sales.packages, 100, 1, 100000);
      const capsulesPerPack = this.getNumber(this.sales.packSize, 30, 1, 1000);
      const periodMonths = this.getNumber(this.sales.period, 6, 1, 24);
      const bufferPercent = this.getNumber(this.sales.buffer, 10, 0, 100);
      const packagesPeriod = Math.round(packagesPerMonth * periodMonths);
      const monthlyCapsules = Math.round(packagesPerMonth * capsulesPerPack);
      const annualCapsules = Math.round(monthlyCapsules * 12);
      const forecastCapsules = Math.ceil(monthlyCapsules * periodMonths * (1 + bufferPercent / 100));
      const requestedWeight = this.sales.weight?.value || 'standard';
      const customProject = projectType !== 'private'
        || formula !== 'standard'
        || scent === 'custom'
        || shape !== '1'
        || packaging === 'custom'
        || requestedWeight !== 'standard';
      const quoteVolume = customProject
        ? Math.max(500000, this.roundUp(forecastCapsules, 50000))
        : Math.max(25000, this.roundUp(forecastCapsules, 5000));
      const weightUnlocked = quoteVolume >= 500000;
      if (this.sales.weight) {
        if (!weightUnlocked) this.sales.weight.value = 'standard';
        this.sales.weight.disabled = !weightUnlocked;
      }
      if (this.sales.weightNote) {
        this.sales.weightNote.textContent = weightUnlocked
          ? this.dataset.weightUnlockedNote
          : this.dataset.weightLockedNote;
      }
      const weight = this.sales.weight?.value || 'standard';
      const customWeightActive = weightUnlocked && weight === 'other';
      if (this.sales.customWeightField) this.sales.customWeightField.hidden = !customWeightActive;
      if (this.sales.customWeight) this.sales.customWeight.disabled = !customWeightActive;
      const customWeightValue = Number(this.sales.customWeight?.value);
      const weightLabel = customWeightActive && Number.isFinite(customWeightValue) && customWeightValue > 0
        ? `${new Intl.NumberFormat(this.getLocale(), { maximumFractionDigits: 1 }).format(customWeightValue)} g`
        : this.optionLabel(this.sales.weight);
      const routeName = customProject ? this.dataset.routePrivateCustom : this.dataset.routePrivateBase;
      const routeText = customProject ? this.dataset.routePrivateCustomText : this.dataset.routePrivateBaseText;

      this.salesCalculation = {
        kind: 'sales', projectType, projectLabel, formula,
        formulaLabel: this.optionLabel(this.sales.formula), scent,
        scentLabel: this.optionLabel(this.sales.scent), weight,
        weightLabel, weightUnlocked, shape, shapeLabel,
        packaging, packagingLabel,
        packagingFormValue, brandingMode, brand, languages, packagesPerMonth,
        capsulesPerPack, periodMonths, bufferPercent, packagesPeriod,
        monthlyCapsules, annualCapsules, forecastCapsules, quoteVolume,
        customProject, routeName,
      };

      this.sales.packagesPeriod.textContent = this.formatNumber(packagesPeriod);
      this.sales.monthly.textContent = this.formatNumber(monthlyCapsules);
      this.sales.annual.textContent = this.formatNumber(annualCapsules);
      this.sales.forecast.textContent = this.formatNumber(forecastCapsules);
      this.sales.quote.textContent = `${this.formatNumber(quoteVolume)} ${this.dataset.capsuleUnit}`;
      this.sales.route.textContent = routeName;
      this.sales.routeText.textContent = routeText;
      this.updateConceptPreview(this.salesCalculation);
      this.hideConfirmations();
    }

    updateConceptPreview(calculation) {
      if (this.sales.previewPod) {
        this.sales.previewPod.dataset.model = calculation.shape;
        this.sales.previewPod.classList.toggle('ff-b2b-pod-sprite--current', Number(calculation.shape) > 6);
      }
      if (this.sales.previewPack) this.sales.previewPack.dataset.packaging = calculation.packaging;
      if (this.sales.previewBadge) this.sales.previewBadge.textContent = calculation.projectLabel;
      if (this.sales.previewBrand) this.sales.previewBrand.textContent = calculation.brand;
      if (this.sales.previewPackaging) this.sales.previewPackaging.textContent = calculation.packagingLabel;
      if (this.sales.previewPackSize) this.sales.previewPackSize.textContent = this.formatNumber(calculation.capsulesPerPack);
      if (this.sales.previewProject) this.sales.previewProject.textContent = calculation.projectLabel;
      if (this.sales.previewFormula) this.sales.previewFormula.textContent = calculation.formulaLabel;
      if (this.sales.previewShape) this.sales.previewShape.textContent = calculation.shapeLabel;
      if (this.sales.previewPackagingSummary) this.sales.previewPackagingSummary.textContent = calculation.packagingLabel;
      if (this.sales.previewLanguages) this.sales.previewLanguages.textContent = calculation.languages.join(', ') || '–';
    }

    hideConfirmations() {
      if (this.usage?.confirmation) this.usage.confirmation.hidden = true;
      if (this.sales?.confirmation) this.sales.confirmation.hidden = true;
    }

    capitalize(value) {
      return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
    }

    getQuantityRange(quantity) {
      if (quantity < 10000) return '';
      if (quantity < 25000) return '10.000 - 25.000';
      if (quantity < 50000) return '25.000 - 50.000';
      if (quantity < 100000) return '50.000 - 100.000';
      if (quantity < 500000) return '100.000 - 500.000';
      return '500.000+';
    }

    setCheckboxes(form, fieldName, selectedValues) {
      const values = new Set(selectedValues.filter(Boolean));
      form.querySelectorAll(`[name="${fieldName}"]`).forEach((input) => {
        input.checked = values.has(input.value);
      });
    }

    addToQuote(calculation, confirmation) {
      const formSection = document.querySelector('#ff-b2b-form');
      const form = formSection?.querySelector('form');
      if (!form || !calculation) return;

      const inquiryType = form.querySelector('[name="contact[Type aanvraag]"]');
      const quantityRange = form.querySelector('[name="contact[Indicatie aantallen]"]');
      const exactQuantity = form.querySelector('[name="contact[Exact gewenst aantal]"]');
      const calculationField = form.querySelector('[name="contact[Volumeberekening]"]');
      const packagingField = form.querySelector('[name="contact[Gewenste verpakking]"]');

      if (calculation.kind === 'usage') {
        if (inquiryType) inquiryType.value = 'FashFresh Business Supply';
      } else {
        if (inquiryType) {
          inquiryType.value = calculation.projectType === 'private' ? 'Private Label' : 'OEM / ODM';
        }
        if (packagingField && [...packagingField.options].some((option) => option.value === calculation.packagingFormValue)) {
          packagingField.value = calculation.packagingFormValue;
        }

        const packagingWishes = [this.dataset.formOwnLogo];
        if (calculation.brandingMode === 'artwork') packagingWishes.push(this.dataset.formOwnArtwork);
        if (calculation.brandingMode === 'label') packagingWishes.push(this.dataset.formOwnLabel);
        if (calculation.brandingMode === 'help') packagingWishes.push(this.dataset.formHelpDesign);
        if (calculation.languages.length > 1) packagingWishes.push(this.dataset.formMultilingual);
        if (calculation.packaging === 'custom') packagingWishes.push(this.dataset.formFullCustomPackaging);
        this.setCheckboxes(form, 'contact[Verpakkingswensen]', packagingWishes);

        const extraWishes = [];
        if (calculation.scent !== 'tbd') extraWishes.push(this.dataset.formOwnFragrance);
        if (calculation.formula !== 'standard') extraWishes.push(this.dataset.formFormula);
        if (calculation.projectType !== 'private') extraWishes.push(this.dataset.formSpecs);
        this.setCheckboxes(form, 'contact[Extra wensen]', extraWishes);
      }

      const desiredQuantity = calculation.kind === 'sales'
        ? calculation.forecastCapsules
        : calculation.periodCapsules;
      if (quantityRange) quantityRange.value = this.getQuantityRange(desiredQuantity);
      if (exactQuantity) exactQuantity.value = this.formatNumber(desiredQuantity);
      if (calculationField) calculationField.value = this.getSummary(calculation);

      this.hideConfirmations();
      if (confirmation) confirmation.hidden = false;
      formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.setTimeout(() => inquiryType?.focus({ preventScroll: true }), 500);
    }

    getSummary(calculation) {
      if (calculation.kind === 'sales') {
        return [
          this.dataset.calculatorSalesName,
          `${this.dataset.summaryProject}: ${calculation.projectLabel}`,
          `${this.dataset.summaryBrand}: ${calculation.brand}`,
          `${this.dataset.summaryFormula}: ${calculation.formulaLabel}`,
          `${this.dataset.summaryScent}: ${calculation.scentLabel}`,
          `${this.dataset.summaryWeight}: ${calculation.weightLabel}`,
          `${this.dataset.summaryShape}: ${calculation.shapeLabel}`,
          `${this.dataset.summaryPackaging}: ${calculation.packagingLabel}`,
          `${this.dataset.summaryLanguages}: ${calculation.languages.join(', ') || '–'}`,
          `${calculation.packagesPerMonth} ${this.dataset.packageUnit} ${this.dataset.summaryPackagesMonth}`,
          `${calculation.capsulesPerPack} ${this.dataset.capsuleUnit} ${this.dataset.summaryPerPackage}`,
          `${calculation.periodMonths} ${this.dataset.summaryForecast}`,
          `${calculation.bufferPercent}% ${this.dataset.summaryBuffer}`,
          `${calculation.annualCapsules} ${this.dataset.capsuleUnit} ${this.dataset.summaryPerYear}`,
          `${calculation.quoteVolume} ${this.dataset.capsuleUnit} ${this.dataset.summaryQuote}`,
          calculation.routeName,
        ].join(' | ');
      }

      const routeName = this.dataset[`route${this.capitalize(calculation.route)}`];
      return [
        this.dataset.calculatorUsageName,
        `${calculation.loadsPerDay} ${this.dataset.loadUnit} ${this.dataset.summaryPerDay}`,
        `${calculation.daysPerWeek} ${this.dataset.dayUnit} ${this.dataset.summaryPerWeek}`,
        `${calculation.dose} ${this.dataset.doseUnit}`,
        `${calculation.periodMonths} ${this.dataset.summaryStock}`,
        `${calculation.annualCapsules} ${this.dataset.capsuleUnit} ${this.dataset.summaryPerYear}`,
        `${calculation.quoteVolume} ${this.dataset.capsuleUnit} ${this.dataset.summaryQuote}`,
        routeName,
      ].join(' | ');
    }
  }

  customElements.define('ff-b2b-volume-calculator', FfB2bVolumeCalculator);
})();
